import * as z from "zod";
import { hsaAnswer } from "../hsa-answer";
import { hsaQuestion } from "../hsa-question-type";

// Wire shape of a group as the questionnaire UI renders it: pool questions
// hydrated inline (handle + content), plus any answers already submitted
// against this group's questions so the UI can pre-fill on resume.
//
// `priorAnswers` is an array — each HsaAnswer already carries its
// `questionHandle`, so the client builds a map locally if it needs one.
// In the common case (linear progression, no reload) this array is empty.
const hsaPortalGroup_ = z.object({
  groupHandle: z.string().min(1),
  title: z.string().optional(),
  questions: z
    .array(z.object({ handle: z.string().min(1), question: hsaQuestion }))
    .min(1),
  priorAnswers: z.array(hsaAnswer),
});
export const hsaPortalGroup: z.ZodType<HsaPortalGroup> = hsaPortalGroup_;
export interface HsaPortalGroup extends z.infer<typeof hsaPortalGroup_> {}

export enum HsaSessionStatus {
  IN_PROGRESS = "in_progress",
  COMPLETE = "complete",
}

const hsaSessionState_ = z.discriminatedUnion("status", [
  z.object({
    status: z.literal(HsaSessionStatus.IN_PROGRESS),
    group: hsaPortalGroup_,
  }),
  z.object({ status: z.literal(HsaSessionStatus.COMPLETE) }),
]);
export const hsaSessionState: z.ZodType<HsaSessionState> = hsaSessionState_;
export type HsaSessionState = z.infer<typeof hsaSessionState_>;

// Partner survey-handoff state. Distinct from hsaSessionState because a handoff
// walk can also end DISQUALIFIED (routed to the partner's failure URL).
export enum HsaHandoffSurveyStatus {
  IN_PROGRESS = "in_progress",
  COMPLETE = "complete",
  DISQUALIFIED = "disqualified",
}

const hsaHandoffSurveyState_ = z.discriminatedUnion("status", [
  z.object({
    status: z.literal(HsaHandoffSurveyStatus.IN_PROGRESS),
    group: hsaPortalGroup_,
  }),
  z.object({ status: z.literal(HsaHandoffSurveyStatus.COMPLETE) }),
  z.object({
    status: z.literal(HsaHandoffSurveyStatus.DISQUALIFIED),
    reason: z.string(),
  }),
]);
export const hsaHandoffSurveyState: z.ZodType<HsaHandoffSurveyState> =
  hsaHandoffSurveyState_;
export type HsaHandoffSurveyState = z.infer<typeof hsaHandoffSurveyState_>;
